import { motion } from 'framer-motion';

interface AlienProcessorProps {
  phase: number; // 0-4
}

export function AlienProcessor({ phase }: AlienProcessorProps) {
  const baseSpeed = Math.max(0.3, 1 - phase * 0.15);
  const shapeCount = Math.min(3 + phase * 2, 9);
  const pulseIntensity = 0.3 + phase * 0.15;

  return (
    <div className="relative w-72 h-72 mx-auto">
      <svg viewBox="0 0 300 300" className="w-full h-full">
        {/* Core pulsing circle */}
        <motion.circle
          cx="150"
          cy="150"
          r={20 + phase * 5}
          fill="none"
          stroke="rgba(59, 130, 246, 0.6)"
          strokeWidth="2"
          animate={{
            r: [20 + phase * 5, 25 + phase * 5, 20 + phase * 5],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{ duration: 2 * baseSpeed, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Outer energy ring */}
        <motion.circle
          cx="150"
          cy="150"
          r="120"
          fill="none"
          stroke="rgba(167, 139, 250, 0.15)"
          strokeWidth="1"
          strokeDasharray="8 12"
          animate={{ rotate: 360 }}
          transition={{ duration: 30 * baseSpeed, repeat: Infinity, ease: 'linear' }}
          style={{ transformOrigin: '150px 150px' }}
        />

        {/* Rotating geometric layers */}
        {Array.from({ length: shapeCount }).map((_, i) => {
          const radius = 40 + i * 12;
          const sides = (i % 3) + 3;
          const angle = (2 * Math.PI) / sides;
          const points = Array.from({ length: sides })
            .map((_, j) => {
              const a = angle * j - Math.PI / 2;
              return `${150 + radius * Math.cos(a)},${150 + radius * Math.sin(a)}`;
            })
            .join(' ');

          const direction = i % 2 === 0 ? 360 : -360;
          const speed = (20 + i * 5) * baseSpeed;
          const opacity = i < shapeCount ? pulseIntensity : 0;

          return (
            <motion.polygon
              key={i}
              points={points}
              fill="none"
              stroke={i % 2 === 0 ? 'rgba(59, 130, 246, 0.3)' : 'rgba(167, 139, 250, 0.25)'}
              strokeWidth="1"
              initial={{ opacity: 0, rotate: 0 }}
              animate={{
                opacity: [opacity * 0.5, opacity, opacity * 0.5],
                rotate: direction,
              }}
              transition={{
                opacity: { duration: 3 + i * 0.5, repeat: Infinity, ease: 'easeInOut' },
                rotate: { duration: speed, repeat: Infinity, ease: 'linear' },
              }}
              style={{ transformOrigin: '150px 150px' }}
            />
          );
        })}

        {/* Energy nodes (phase 2+) */}
        {phase >= 2 && Array.from({ length: 6 }).map((_, i) => {
          const a = (Math.PI * 2 * i) / 6;
          const r = 80 + (phase - 2) * 10;
          const cx = 150 + r * Math.cos(a);
          const cy = 150 + r * Math.sin(a);

          return (
            <motion.circle
              key={`node-${i}`}
              cx={cx}
              cy={cy}
              r="3"
              fill="rgba(96, 165, 250, 0.8)"
              animate={{
                r: [2, 4, 2],
                opacity: [0.4, 1, 0.4],
              }}
              transition={{
                duration: 1.5 + i * 0.3,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: i * 0.2,
              }}
            />
          );
        })}

        {/* Connection lines (phase 3+) */}
        {phase >= 3 && Array.from({ length: 6 }).map((_, i) => {
          const a1 = (Math.PI * 2 * i) / 6;
          const a2 = (Math.PI * 2 * ((i + 1) % 6)) / 6;
          const r = 80 + (phase - 2) * 10;

          return (
            <motion.line
              key={`line-${i}`}
              x1={150 + r * Math.cos(a1)}
              y1={150 + r * Math.sin(a1)}
              x2={150 + r * Math.cos(a2)}
              y2={150 + r * Math.sin(a2)}
              stroke="rgba(59, 130, 246, 0.15)"
              strokeWidth="1"
              animate={{ opacity: [0, 0.3, 0] }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.3,
              }}
            />
          );
        })}

        {/* Center glow (phase 4) */}
        {phase >= 4 && (
          <motion.circle
            cx="150"
            cy="150"
            r="40"
            fill="url(#centerGlow)"
            animate={{
              r: [40, 30, 40],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}

        <defs>
          <radialGradient id="centerGlow">
            <stop offset="0%" stopColor="rgba(59, 130, 246, 0.4)" />
            <stop offset="100%" stopColor="rgba(59, 130, 246, 0)" />
          </radialGradient>
        </defs>
      </svg>
    </div>
  );
}
